import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Loader2,
  Check,
  Upload,
  ChevronDown,
  X,
  ArrowLeft,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { uploadBrandingAsset } from '@/services/brandingService';
import { updateDefaultBrandingDefaults } from '@/services/paymentService';
import {
  OVERLAY_TEMPLATES,
  GOOGLE_FONTS_LIST,
  loadGoogleFont,
  renderOverlayToCanvas,
  renderOverlayTemplate,
} from '@/lib/overlayTemplates';
import type { OverlayBuilderConfig, OverlayTemplateId } from '@/types';

interface OverlayBuilderViewProps {
  onSave: () => void;
}

type BuilderStep = 1 | 2 | 3;

const STEP_LABELS: Record<BuilderStep, string> = {
  1: 'Vorlage',
  2: 'Branding',
  3: 'Text',
};

const DEFAULT_CONFIG: OverlayBuilderConfig = {
  templateId: 'bar',
  accentColor: '#1a365d',
  logoUrl: null,
  logoPosition: 'bottom-left',
  logoScale: 0.15,
  text: '',
  fontFamily: 'Montserrat',
  aspectRatio: '4:3', // intern; gespeichert werden immer 4:3 UND 1:1
  barHeight: 0.10,
  barOpacity: 0.85,
  textScale: 1.0,
};

// Templates visible in UI (corner hidden)
const VISIBLE_TEMPLATES = OVERLAY_TEMPLATES.filter((t) => t.id !== 'corner');

const SWATCHES = ['#1a365d', '#1e3a2f', '#4a1942', '#8b2500', '#2d3748', '#000000'];

export const OverlayBuilderView: React.FC<OverlayBuilderViewProps> = ({ onSave }) => {
  const { user } = useAuth();
  const canvas43Ref = useRef<HTMLCanvasElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const fontDropdownRef = useRef<HTMLDivElement>(null);

  const [config, setConfig] = useState<OverlayBuilderConfig>(DEFAULT_CONFIG);
  const [step, setStep] = useState<BuilderStep>(1);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [fontSearch, setFontSearch] = useState('');
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);

  // Close font dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target as Node)) {
        setFontDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredFonts = useMemo(() => {
    if (!fontSearch) return GOOGLE_FONTS_LIST;
    const q = fontSearch.toLowerCase();
    return GOOGLE_FONTS_LIST.filter((f) => f.toLowerCase().includes(q));
  }, [fontSearch]);

  // Render the 4:3 preview on config change. Beim Erstellen werden 4:3 UND 1:1
  // gespeichert (siehe handleCreate) — die Vorschau zeigt nur das Querformat.
  useEffect(() => {
    const render = async () => {
      try {
        if (canvas43Ref.current) {
          await renderOverlayToCanvas({ ...config, aspectRatio: '4:3' }, canvas43Ref.current);
        }
      } catch (error) {
        console.error('[OverlayBuilder] Preview render error:', error);
      }
    };
    const timeout = setTimeout(render, 100);
    return () => clearTimeout(timeout);
  }, [config]);

  const updateConfig = useCallback((updates: Partial<OverlayBuilderConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
    setSaveSuccess(false);
  }, []);

  const handleLogoUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      updateConfig({ logoUrl: url });
      e.target.value = '';
    },
    [updateConfig]
  );

  const handleRemoveLogo = useCallback(() => {
    updateConfig({ logoUrl: null });
  }, [updateConfig]);

  const handleFontSelect = useCallback(
    async (font: string) => {
      await loadGoogleFont(font);
      updateConfig({ fontFamily: font });
      setFontDropdownOpen(false);
      setFontSearch('');
    },
    [updateConfig]
  );

  // Erstellt beide Formate (4:3 + 1:1), speichert sie und setzt das Querformat
  // als Standard-Overlay.
  const handleCreate = useCallback(async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      const [blob43, blob11] = await Promise.all([
        renderOverlayTemplate({ ...config, aspectRatio: '4:3' }),
        renderOverlayTemplate({ ...config, aspectRatio: '1:1' }),
      ]);
      const stamp = Date.now();
      const file43 = new File([blob43], `overlay-${config.templateId}-4x3-${stamp}.png`, { type: 'image/png' });
      const file11 = new File([blob11], `overlay-${config.templateId}-1x1-${stamp}.png`, { type: 'image/png' });

      // 4:3 zuerst — dessen ID wird als Standard-Overlay gesetzt.
      const asset43 = await uploadBrandingAsset(user.id, file43, 'overlay');
      await uploadBrandingAsset(user.id, file11, 'overlay');
      await updateDefaultBrandingDefaults(user.id, { overlayAssetId: asset43.id });

      setSaveSuccess(true);
      setTimeout(() => onSave(), 1000);
    } catch (error) {
      console.error('[OverlayBuilder] Failed to create overlay:', error);
      alert('Overlay konnte nicht erstellt werden.');
    } finally {
      setIsSaving(false);
    }
  }, [user, config, onSave]);

  const goNext = useCallback(() => setStep((s) => (s < 3 ? ((s + 1) as BuilderStep) : s)), []);
  const goBack = useCallback(() => setStep((s) => (s > 1 ? ((s - 1) as BuilderStep) : s)), []);

  // ==========================================================================
  // Step content
  // ==========================================================================

  const stepHeading = (title: string, subtitle: string) => (
    <div>
      <h3 className="text-base font-bold text-foreground">{title}</h3>
      <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );

  const renderTemplateStep = () => (
    <>
      {stepHeading('Vorlage & Größe', 'Wähle das Layout und passe die Größen an.')}

      <div className="grid grid-cols-2 gap-3">
        {VISIBLE_TEMPLATES.map((tpl) => {
          const active = config.templateId === tpl.id;
          return (
            <button
              key={tpl.id}
              type="button"
              onClick={() => updateConfig({ templateId: tpl.id })}
              className={cn(
                'flex flex-col items-center gap-2 rounded-xl border-2 p-3 text-center transition-all',
                active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
              )}
            >
              <TemplateThumb templateId={tpl.id} accentColor={config.accentColor} />
              <span className={cn('text-xs font-semibold', active ? 'text-primary' : 'text-foreground')}>
                {tpl.label}
              </span>
            </button>
          );
        })}
      </div>

      {config.templateId === 'bar' && (
        <div className="space-y-4 rounded-xl border border-border bg-muted/20 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Leisten-Einstellungen</p>
          <SliderRow
            label="Höhe"
            value={`${Math.round((config.barHeight ?? 0.1) * 100)}%`}
            min={0.05}
            max={0.2}
            step={0.01}
            current={config.barHeight ?? 0.1}
            onChange={(v) => updateConfig({ barHeight: v })}
          />
          <SliderRow
            label="Deckkraft"
            value={`${Math.round((config.barOpacity ?? 0.85) * 100)}%`}
            min={0.3}
            max={1}
            step={0.05}
            current={config.barOpacity ?? 0.85}
            onChange={(v) => updateConfig({ barOpacity: v })}
          />
        </div>
      )}

      {config.templateId !== 'bar' && (
        <p className="text-xs text-muted-foreground">
          Für diese Vorlage gibt es keine weiteren Größen-Einstellungen.
        </p>
      )}
    </>
  );

  const renderBrandingStep = () => (
    <>
      {stepHeading('Branding', 'Akzentfarbe, Logo und Logo-Position.')}

      {/* Accent color */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Akzentfarbe</Label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="color"
            value={config.accentColor}
            onChange={(e) => updateConfig({ accentColor: e.target.value })}
            className="h-10 w-10 cursor-pointer rounded-lg border border-border p-0.5"
          />
          <Input
            value={config.accentColor}
            onChange={(e) => {
              const val = e.target.value;
              if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) updateConfig({ accentColor: val });
            }}
            className="w-28 font-mono text-sm"
            placeholder="#1a365d"
          />
          <div className="flex gap-1.5">
            {SWATCHES.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => updateConfig({ accentColor: color })}
                className={cn(
                  'h-6 w-6 rounded-full border-2 transition-all',
                  config.accentColor.toLowerCase() === color ? 'border-primary scale-110' : 'border-transparent'
                )}
                style={{ backgroundColor: color }}
                aria-label={color}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Logo */}
      <div className="space-y-3">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Logo</Label>
        <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />

        {config.logoUrl ? (
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
              <img src={config.logoUrl} alt="Logo" className="h-full w-full object-contain p-1.5" />
            </div>
            <div className="flex flex-col gap-2">
              <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
                <Upload className="mr-1.5 h-3.5 w-3.5" /> Ändern
              </Button>
              <Button variant="ghost" size="sm" onClick={handleRemoveLogo} className="text-destructive hover:text-destructive">
                <X className="mr-1.5 h-3.5 w-3.5" /> Entfernen
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => logoInputRef.current?.click()} className="w-full gap-2">
            <Upload className="h-4 w-4" /> Logo hochladen
          </Button>
        )}

        {config.logoUrl && (
          <>
            <div>
              <Label className="text-xs text-muted-foreground">Position</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {(
                  [
                    { key: 'bottom-left', label: 'Links', match: ['bottom-left', 'top-left'] },
                    { key: 'bottom-right', label: 'Rechts', match: ['bottom-right', 'top-right'] },
                  ] as const
                ).map(({ key, label, match }) => {
                  const active = (match as readonly string[]).includes(config.logoPosition);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => updateConfig({ logoPosition: key })}
                      className={cn(
                        'rounded-lg border-2 py-2 text-xs font-medium transition-colors',
                        active ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <SliderRow
              label="Logo-Größe"
              value={`${Math.round(config.logoScale * 100)}%`}
              min={0.05}
              max={0.8}
              step={0.01}
              current={config.logoScale}
              onChange={(v) => updateConfig({ logoScale: v })}
            />
          </>
        )}
      </div>
    </>
  );

  const renderTextStep = () => (
    <>
      {stepHeading('Text & Schrift', 'URL oder Slogan, Größe und Schriftart.')}

      <div className="space-y-1.5">
        <Label htmlFor="overlay-text" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          URL oder Slogan
        </Label>
        <Input
          id="overlay-text"
          value={config.text}
          onChange={(e) => updateConfig({ text: e.target.value })}
          placeholder="www.meine-immobilien.de"
        />
      </div>

      <SliderRow
        label="Textgröße"
        value={`${Math.round((config.textScale ?? 1) * 100)}%`}
        min={0.5}
        max={2}
        step={0.1}
        current={config.textScale ?? 1}
        onChange={(v) => updateConfig({ textScale: v })}
      />

      {/* Font selection */}
      <div ref={fontDropdownRef} className="relative space-y-1.5">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Schriftart</Label>
        <button
          type="button"
          onClick={() => setFontDropdownOpen(!fontDropdownOpen)}
          className="flex w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm transition-colors hover:bg-accent"
        >
          <span style={{ fontFamily: `'${config.fontFamily}', sans-serif` }}>{config.fontFamily}</span>
          <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', fontDropdownOpen && 'rotate-180')} />
        </button>

        {fontDropdownOpen && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg">
            <div className="p-2">
              <Input
                value={fontSearch}
                onChange={(e) => setFontSearch(e.target.value)}
                placeholder="Schriftart suchen..."
                className="h-8 text-sm"
                autoFocus
              />
            </div>
            <div className="max-h-52 overflow-y-auto px-1 pb-1">
              {filteredFonts.slice(0, 60).map((font) => (
                <button
                  key={font}
                  type="button"
                  onClick={() => handleFontSelect(font)}
                  className={cn(
                    'flex w-full items-center justify-between rounded px-3 py-1.5 text-left text-sm transition-colors',
                    config.fontFamily === font ? 'bg-primary/10 text-primary' : 'hover:bg-accent'
                  )}
                >
                  <span>{font}</span>
                  {config.fontFamily === font && <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
                </button>
              ))}
              {filteredFonts.length === 0 && (
                <p className="py-3 text-center text-xs text-muted-foreground">Keine Schriftart gefunden</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );

  // ==========================================================================
  // Chrome
  // ==========================================================================

  const renderStepIndicator = () => (
    <div className="mx-auto flex w-full max-w-md items-start">
      {([1, 2, 3] as BuilderStep[]).map((s, i) => {
        const isDone = s < step;
        const isCurrent = s === step;
        return (
          <React.Fragment key={s}>
            {i > 0 && (
              <div className={cn('mt-[19px] h-0.5 min-w-4 flex-1 rounded-full sm:mx-2', isDone || isCurrent ? 'bg-primary' : 'bg-border')} />
            )}
            <div className="flex w-16 flex-col items-center gap-1.5 sm:w-24">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-shadow',
                  isDone && 'bg-primary text-primary-foreground',
                  isCurrent && 'bg-primary text-primary-foreground ring-[5px] ring-primary/15',
                  !isDone && !isCurrent && 'border-2 border-border bg-card text-muted-foreground'
                )}
              >
                {isDone ? <Check className="h-4 w-4" /> : s}
              </div>
              <span
                className={cn(
                  'text-center text-xs font-semibold leading-tight',
                  isDone || isCurrent ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {STEP_LABELS[s]}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-foreground">Overlay Builder</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Erstelle ein Overlay aus einer Vorlage — es wird automatisch als Querformat (4:3) und quadratisch (1:1)
          gespeichert und als Standard gesetzt.
        </p>
      </div>

      {renderStepIndicator()}

      {/* Two columns: settings | preview */}
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(340px,420px)_1fr]">
        {/* Left: current step's settings */}
        <div className="space-y-5 rounded-xl border border-border bg-card p-5">
          {step === 1 && renderTemplateStep()}
          {step === 2 && renderBrandingStep()}
          {step === 3 && renderTextStep()}
        </div>

        {/* Right: sticky live preview (both formats) */}
        <div className="lg:sticky lg:top-6 self-start">
          <div className="space-y-4 rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Live-Vorschau</p>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Querformat · 4:3</span>
              <div className="overflow-hidden rounded-lg bg-[repeating-conic-gradient(#e5e7eb_0%_25%,transparent_0%_50%)] bg-[length:20px_20px]">
                <canvas ref={canvas43Ref} className="block h-auto w-full" />
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground">
              Transparenter Hintergrund — wird über deine Bilder gelegt. Gespeichert werden Querformat (4:3) und
              quadratisch (1:1).
            </p>
          </div>
        </div>
      </div>

      {/* Footer navigation */}
      <div className="flex items-center justify-between border-t border-border pt-4">
        {step > 1 ? (
          <Button variant="ghost" onClick={goBack} disabled={isSaving} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Button>
        ) : (
          <span />
        )}

        {step < 3 ? (
          <Button onClick={goNext} className="gap-2">
            Weiter <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={handleCreate} disabled={isSaving || saveSuccess} className="gap-2" size="lg">
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Wird erstellt…
              </>
            ) : saveSuccess ? (
              <>
                <Check className="h-4 w-4" /> Erstellt!
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Overlay erstellen
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Slider row (label + value + shadcn Slider)
// ============================================================================

const SliderRow: React.FC<{
  label: string;
  value: string;
  min: number;
  max: number;
  step: number;
  current: number;
  onChange: (v: number) => void;
}> = ({ label, value, min, max, step, current, onChange }) => (
  <div>
    <div className="mb-1.5 flex items-center justify-between">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <span className="font-mono text-xs text-foreground">{value}</span>
    </div>
    <Slider min={min} max={max} step={step} value={[current]} onValueChange={([v]) => onChange(v)} />
  </div>
);

// ============================================================================
// Template Thumbnail Component (inline mini-preview)
// ============================================================================

const TemplateThumb: React.FC<{
  templateId: OverlayTemplateId;
  accentColor: string;
}> = ({ templateId, accentColor }) => {
  const w = 80;
  const h = 60;

  const renderThumb = () => {
    switch (templateId) {
      case 'bar':
        return (
          <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
            <rect x={0} y={h - 8} width={w} height={8} fill={accentColor} opacity={0.85} />
            <rect x={3} y={h - 6} width={8} height={4} rx={1} fill="white" opacity={0.7} />
            <rect x={30} y={h - 5} width={30} height={2} rx={1} fill="white" opacity={0.5} />
          </svg>
        );
      case 'corner':
        return (
          <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
            <rect x={0} y={h - 8} width={w} height={8} fill={accentColor} opacity={0.85} />
            <rect x={0} y={h - 25} width={3} height={17} fill={accentColor} opacity={0.7} />
            <rect x={4} y={h - 6} width={8} height={4} rx={1} fill="white" opacity={0.7} />
            <rect x={40} y={h - 5} width={30} height={2} rx={1} fill="white" opacity={0.5} />
          </svg>
        );
      case 'frame':
        return (
          <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
            <rect x={0} y={0} width={w} height={2} fill={accentColor} />
            <rect x={0} y={0} width={2} height={h} fill={accentColor} />
            <rect x={w - 2} y={0} width={2} height={h} fill={accentColor} />
            <rect x={0} y={h - 8} width={w} height={8} fill={accentColor} />
            <rect x={4} y={h - 6} width={8} height={4} rx={1} fill="white" opacity={0.7} />
            <rect x={30} y={h - 5} width={30} height={2} rx={1} fill="white" opacity={0.5} />
          </svg>
        );
    }
  };

  return (
    <div className="h-[60px] w-20 overflow-hidden rounded border border-border bg-muted/50">{renderThumb()}</div>
  );
};
